import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, Loader2 } from 'lucide-react';
import { attendanceService, type PunchLogWithUser } from '@/services/attendance.service';
import { useInvalidateAttendance } from '@/hooks/attendance/useInvalidateAttendance';

// ── Helpers ────────────────────────────────────────────────────────────────────

function toISTDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  });
}

// ── Props ──────────────────────────────────────────────────────────────────────

interface Props {
  log:       PunchLogWithUser;
  onClose:   () => void;
  onSuccess: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function DeletePunchModal({ log, onClose, onSuccess }: Props) {
  const fullName  = [log.first_name, log.last_name].filter(Boolean).join(' ') || '–';
  const isIn      = log.punch_type === 'check_in';
  const timeLabel = toISTDateTime(log.punch_time);

  const [reason,     setReason]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const invalidateAttendance = useInvalidateAttendance();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Reason is required.');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await attendanceService.deletePunch(log.id, reason.trim());
      invalidateAttendance();
      onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete punch');
      setSubmitting(false);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-modal-nested flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-2xl">

        {/* Header */}
        <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-50 border border-rose-100 mt-0.5">
            <Trash2 className="h-4 w-4 text-rose-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-slate-800">Delete Punch</h2>
            <p className="text-sm font-semibold text-slate-600 mt-0.5">{fullName}</p>
            <p className="text-xs text-slate-400">This action cannot be undone.</p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">

          {/* Punch summary */}
          <div className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
            isIn
              ? 'border-emerald-200 bg-emerald-50'
              : 'border-rose-200 bg-rose-50'
          }`}>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider ${
              isIn ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
            }`}>
              {isIn ? 'IN' : 'OUT'}
            </span>
            <p className="text-sm font-semibold text-slate-700">{timeLabel}</p>
          </div>

          {/* Reason */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Reason for Deletion <span className="text-rose-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              rows={3}
              placeholder="Please provide a reason for deleting this punch…"
              className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-200"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 rounded-lg bg-rose-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-700 transition-colors disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              {submitting ? 'Deleting…' : 'Delete Punch'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
